import { Stack, Table, TableBody, TableCell, TableContainer, TableHead, Typography } from "@mui/material";
import { OrderActions } from "../../../../shared/components/order-actions/OrderActions";
import { Props } from "./Props";
import { AssetType } from "../../../../shared/types/AssetType";
import { OrderModel } from "../../../portfolio-info/types/OrderModel";
import { dateTimeConversionService } from "../../../../shared/services/DateTimeConversionService";
import { TableBodyRow, TableHeaderRow } from "../../../../screens/shared/views";
import {
    FUTURES_TABLE_ROW_PADDING_Y,
    SPOT_TABLE_ROW_PADDING_Y
} from "../../../../screens/shared/constants/table.constants";
import { numberFormatters } from "../../../../screens/shared/utils/numberFormatters";
import { OrderState } from "../../../../shared/types/OrderState";

export function UnderlyingAssetsTable({
    underlyingAsset,
    spotPrice,
    futuresPrice,
    addFutureToPortfolio,
}: Props){
    if (underlyingAsset === undefined)
        return null;

    async function handleAddItem(size: number, type: AssetType) {
        if (type !== AssetType.Spot && type !== AssetType.Futures)
            throw new Error("Asset type is not supported.");
        
        const futureOrder: OrderModel = new OrderModel({
            externalId: 0,
            exchangeOrderId: null,
            internalId: crypto.randomUUID(),
            state: OrderState.Disabled,
            openedPositionAt: dateTimeConversionService.getCurrentUtcDate(),
            expireAt: null,
            impliedVolatilityAtOpen: null,
            customImpliedVolatility: null,
            symbol: `${underlyingAsset!.BaseAsset}/USDT`,
            size: size,
            openPrice: underlyingAsset!.Price ?? 0,
            markPrice: underlyingAsset!.Price ?? 0,
            markImpliedVolatility: null,
            type: type,
            strike: null,
            underlyingPriceAtOpen: null,
            delta: Math.sign(size),
            gamma: null,
            vega: null,
            theta: null,
            comment: null,
            expirationPrice: null,
            dateForPricing: dateTimeConversionService.getCurrentUtcDate(),
            impliedVolatilityForPricing: null,
            underlyingPriceForPricing: underlyingAsset!.Price ?? 0,
        });

        await addFutureToPortfolio(futureOrder);
    }
    
    return (
        <Stack direction="column" spacing={0}>
            <Typography variant="subtitle1">
                <b>Spot assets</b>
            </Typography>
            <TableContainer>
                <Table sx={{ width: 'auto' }} size="small" aria-label="caption table">
                    <TableHead>
                        <TableHeaderRow>
                            <TableCell>Actions</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Symbol</TableCell>
                            <TableCell>Price</TableCell>
                        </TableHeaderRow>
                    </TableHead>
                    <TableBody>
                        <TableBodyRow
                            key={underlyingAsset.Symbol}
                            paddingY={SPOT_TABLE_ROW_PADDING_Y}
                        >
                            <TableCell>
                                <OrderActions
                                    action={(value: number) => handleAddItem(value, AssetType.Spot)}
                                />
                            </TableCell>
                            <TableCell>Spot</TableCell>
                            <TableCell>{underlyingAsset.BaseAsset}/USDT</TableCell>
                            <TableCell align="right">
                                {numberFormatters.format2(underlyingAsset.Price)}
                            </TableCell>
                        </TableBodyRow>
                    </TableBody>
                </Table>
            </TableContainer>
            
            <Typography variant="subtitle1" sx={{ marginTop:'20px' }}>
                <b>Futures assets</b>
            </Typography>
            <TableContainer>
                <Table sx={{ width: 'auto' }} size="small" aria-label="caption table">
                    <TableHead>
                        <TableHeaderRow>
                            <TableCell>Actions</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Symbol</TableCell>
                            <TableCell>Price</TableCell>
                        </TableHeaderRow>
                    </TableHead>
                    <TableBody>
                        <TableBodyRow
                            key={underlyingAsset.Symbol}
                            paddingY={FUTURES_TABLE_ROW_PADDING_Y}
                        >
                            <TableCell>
                                <OrderActions
                                    action={(value: number) => handleAddItem(value, AssetType.Futures)}
                                />
                            </TableCell>
                            <TableCell>Futures</TableCell>
                            <TableCell>{underlyingAsset.BaseAsset}/USDT</TableCell>
                            <TableCell align="right">
                                {numberFormatters.format2(futuresPrice)}
                            </TableCell>
                        </TableBodyRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </Stack>
    )
}
